import api from '../../API/baseURL';
import { handleAuthErrors } from '../../utils/vsCodeUtils';

// API endpoints for the Whop payment provider (parallel the Stripe ones).
const WHOP_ENDPOINTS = {
  GET_PROVIDERS: '/payment/providers',
  CREATE_CHECKOUT: '/payment/whop/create_checkout_url',
  CREATE_LIFETIME_CHECKOUT: '/payment/whop/create_lifetime_checkout',
  UPGRADE_PLAN: '/payment/whop/upgrade_plan',
  CANCEL_SUBSCRIPTION: '/payment/whop/cancel_subscription_plan',
  GET_INVOICES: '/payment/whop/get_user_invoices',
  GET_MANAGE_URL: '/payment/whop/get_manage_url'
};

const authHeaders = () => {
  const token = localStorage.getItem('web_access_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// Provider config (enabled / default / allow_selection / whop_sandbox).
export const getPaymentProviders = async () => {
  try {
    const response = await api.get(WHOP_ENDPOINTS.GET_PROVIDERS, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    console.error('Error fetching payment providers:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Create a Whop hosted checkout for a recurring plan. Returns { url }.
export const createWhopCheckout = async ({ plan_id, email, user_id, success_url }) => {
  try {
    const response = await api.post(
      WHOP_ENDPOINTS.CREATE_CHECKOUT,
      { plan_id, email, user_id, success_url },
      { headers: authHeaders() }
    );
    return response.data; // { url }
  } catch (error) {
    console.error('Error creating Whop checkout:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// In-place upgrade: auto-charge the saved card the prorated difference and switch
// the recurring plan. Returns { success: true, ... } on a completed auto-charge,
// or { requires_checkout: true, checkout_url } to fall back to a hosted checkout
// (no saved card / declined). Mirrors the Stripe upgrade flow.
export const upgradeWhopPlan = async ({ plan_id, email, user_id, success_url }) => {
  try {
    const response = await api.post(
      WHOP_ENDPOINTS.UPGRADE_PLAN,
      { plan_id, email, user_id, success_url },
      { headers: authHeaders() }
    );
    return response.data; // { success } | { requires_checkout, checkout_url }
  } catch (error) {
    console.error('Error upgrading Whop plan:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Create a Whop hosted checkout for the one-time Lifetime plan. Returns { url }.
export const createWhopLifetimeCheckout = async ({ email, user_id, success_url }) => {
  try {
    const response = await api.post(
      WHOP_ENDPOINTS.CREATE_LIFETIME_CHECKOUT,
      { email, user_id, success_url },
      { headers: authHeaders() }
    );
    return response.data; // { url }
  } catch (error) {
    console.error('Error creating Whop lifetime checkout:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Cancel the user's active Whop membership(s).
export const cancelWhopSubscription = async () => {
  try {
    const response = await api.delete(WHOP_ENDPOINTS.CANCEL_SUBSCRIPTION, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    console.error('Error cancelling Whop subscription:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Hosted Whop billing-management URL (update payment method / manage sub).
export const getWhopManageUrl = async () => {
  try {
    const response = await api.get(WHOP_ENDPOINTS.GET_MANAGE_URL, { headers: authHeaders() });
    return response.data; // { url }
  } catch (error) {
    console.error('Error fetching Whop manage URL:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// List the user's Whop payments (invoice / billing-history equivalent).
export const getWhopInvoices = async () => {
  try {
    const response = await api.get(WHOP_ENDPOINTS.GET_INVOICES, { headers: authHeaders() });
    return response.data; // { data: [...] }
  } catch (error) {
    console.error('Error fetching Whop invoices:', error);
    handleAuthErrors(error);
  }
};
